import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClasses, getSessions, getPromotions, getStudents,
  postPromotionsPreview, postPromotionsApply, postPromotionsRevert, postGraduationRun,
  postPromotionsManual,
} from '@/api/owner';
import {
  GraduationCap, ArrowRight, Play, RotateCcw, CheckCircle2, AlertCircle,
  Users, UserCheck, UserX, Bell,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const fmtClass = (c: any) => (c?.name ?? '') + (c?.arm ? ' · ' + c.arm : '');

type Mode = 'auto' | 'manual';
type Decision = 'promote' | 'repeat' | 'graduate';

export function PromotionPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('manual');

  // ─── shared: class list ───
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then((r: any) => r.data?.data ?? r.data) });
  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then((r: any) => r.data?.data ?? r.data) });
  const classes = arr<any>(classesQ.data);
  const sessions = arr<any>(sessionsQ.data);

  // ═══════════════════════════════════════════════════════════
  // MANUAL MODE state
  // ═══════════════════════════════════════════════════════════
  const [manualFromClassId, setManualFromClassId] = useState<number | ''>('');
  const [manualToClassId, setManualToClassId]     = useState<number | ''>('');
  const [manualDecision, setManualDecision]       = useState<Decision>('promote');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(new Set());
  const [manualReason, setManualReason]           = useState('');

  const studentsInFromClassQ = useQuery({
    queryKey: ['students-in-class', manualFromClassId],
    queryFn: () => getStudents({ class_id: manualFromClassId, per_page: 500 }).then((r: any) => r.data?.data ?? r.data),
    enabled: !!manualFromClassId,
  });
  const fromClassStudents = arr<any>(studentsInFromClassQ.data);

  const manualMut = useMutation({
    mutationFn: () => postPromotionsManual({
      from_class_id: Number(manualFromClassId),
      to_class_id: manualDecision === 'graduate' ? undefined : Number(manualToClassId),
      student_ids: selectedStudentIds.size > 0 ? Array.from(selectedStudentIds) : undefined,
      decision: manualDecision,
      reason: manualReason || undefined,
    }),
    onSuccess: (r: any) => {
      const d = r.data?.data ?? r.data;
      alert(
        `✅ ${d?.moved ?? 0} student(s) ${manualDecision}ed\n` +
        `${d?.notified ?? 0} notification(s) sent to parents/students`
      );
      setSelectedStudentIds(new Set());
      setManualReason('');
      qc.invalidateQueries({ queryKey: ['students-in-class', manualFromClassId] });
      qc.invalidateQueries({ queryKey: ['owner-students'] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Manual promotion failed'),
  });

  const toggleStudent = (id: number) => {
    const next = new Set(selectedStudentIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedStudentIds(next);
  };

  const selectAll = () => setSelectedStudentIds(new Set(fromClassStudents.map((s: any) => s.id)));
  const clearAll  = () => setSelectedStudentIds(new Set());

  // ═══════════════════════════════════════════════════════════
  // AUTO MODE state (existing annual-results flow)
  // ═══════════════════════════════════════════════════════════
  const [classId, setClassId]         = useState<number | ''>('');
  const [sessionId, setSessionId]     = useState<number | ''>('');
  const [targetClassId, setTargetClassId] = useState<number | ''>('');
  const [passMark, setPassMark]       = useState('40');
  const [preview, setPreview]         = useState<any>(null);
  const [isGraduation, setIsGraduation] = useState(false);

  const historyQ = useQuery({
    queryKey: ['owner-promotions', classId, sessionId],
    queryFn: () => getPromotions({ class_id: classId || undefined, session_id: sessionId || undefined }).then((r: any) => r.data?.data ?? r.data),
  });
  const history = arr<any>(historyQ.data);

  const previewM = useMutation({
    mutationFn: () => postPromotionsPreview({ class_id: Number(classId), session_id: Number(sessionId) }),
    onSuccess: (r: any) => setPreview(r.data?.data ?? r.data),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Preview failed'),
  });
  const applyM = useMutation({
    mutationFn: () => postPromotionsApply({
      class_id: Number(classId), session_id: Number(sessionId),
      target_class_id: targetClassId ? Number(targetClassId) : undefined,
      pass_mark: Number(passMark) || undefined,
    }),
    onSuccess: (r: any) => {
      alert(r.data?.message ?? 'Promotions applied');
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['owner-promotions'] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Apply failed'),
  });
  const revertM = useMutation({
    mutationFn: (id: number) => postPromotionsRevert(id, { reason: 'Reverted from promotions page' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner-promotions'] }); alert('Reverted'); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Revert failed'),
  });
  const gradM = useMutation({
    mutationFn: () => postGraduationRun({ class_id: Number(classId), session_id: Number(sessionId) }),
    onSuccess: (r: any) => { alert(r.data?.message ?? 'Graduation run complete'); qc.invalidateQueries({ queryKey: ['owner-promotions'] }); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Graduation failed'),
  });

  const targetOptions = useMemo(() => {
    // Suggest next class automatically based on current class name if user hasn't picked one
    if (!manualFromClassId) return classes;
    const from = classes.find((c: any) => c.id === Number(manualFromClassId));
    if (!from) return classes;
    // If class name has a digit, suggest name+1
    const m = from.name.match(/^(.*?)(\d+)$/);
    if (m) {
      const nextNum = Number(m[2]) + 1;
      const nextName = m[1] + nextNum;
      return classes.filter((c: any) => c.name === nextName || c.name === from.name);
    }
    return classes;
  }, [classes, manualFromClassId]);

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Academics</p>
        <h1 className="text-3xl font-extrabold text-slate-900">Promotions & Class changes</h1>
        <p className="text-slate-500 text-sm mt-1">
          Move students between classes, repeat a class, or graduate a final-year cohort. Parents are notified automatically.
        </p>
      </div>

      {/* MODE SWITCHER */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        <button onClick={() => setMode('manual')}
          className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
            mode === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          <Users className="w-4 h-4 inline mr-1" /> Manual / Bulk move
        </button>
        <button onClick={() => setMode('auto')}
          className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
            mode === 'auto' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          <BarChartIcon /> Auto (from annual results)
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════
          MANUAL MODE
          ═══════════════════════════════════════════════════════ */}
      {mode === 'manual' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Manual / Bulk class change</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">From class *</label>
                <select value={manualFromClassId}
                  onChange={(e) => { setManualFromClassId(e.target.value ? Number(e.target.value) : ''); setSelectedStudentIds(new Set()); setManualToClassId(''); }}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Select class…</option>
                  {classes.map((c: any) => <option key={c.id} value={c.id}>{fmtClass(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Decision *</label>
                <select value={manualDecision} onChange={(e) => setManualDecision(e.target.value as Decision)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="promote">Promote → next class</option>
                  <option value="repeat">Repeat → same class</option>
                  <option value="graduate">Graduate → alumni</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {manualDecision === 'repeat' ? 'Repeat in *' : manualDecision === 'graduate' ? 'To class (disabled)' : 'To class *'}
                </label>
                <select value={manualDecision === 'graduate' ? '' : manualToClassId}
                  onChange={(e) => setManualToClassId(e.target.value ? Number(e.target.value) : '')}
                  disabled={manualDecision === 'graduate'}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
                  <option value="">Select class…</option>
                  {targetOptions.map((c: any) => <option key={c.id} value={c.id}>{fmtClass(c)}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Reason (optional, saved in audit log)</label>
              <input value={manualReason} onChange={(e) => setManualReason(e.target.value)}
                placeholder="e.g. End of first term reshuffle, parent request, etc."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* STUDENT SELECTION */}
          {manualFromClassId && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    Students in {fmtClass(classes.find((c: any) => c.id === Number(manualFromClassId)))}
                  </p>
                  <p className="text-xs text-slate-500">
                    {fromClassStudents.length} enrolled · {selectedStudentIds.size === 0
                      ? 'move ALL if none selected'
                      : `${selectedStudentIds.size} selected`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={selectAll} disabled={fromClassStudents.length === 0}
                    className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline disabled:opacity-50">Select all</button>
                  <button onClick={clearAll} disabled={selectedStudentIds.size === 0}
                    className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline disabled:opacity-50">Clear</button>
                </div>
              </div>

              {studentsInFromClassQ.isLoading ? (
                <p className="p-6 text-center text-sm text-slate-500">Loading students…</p>
              ) : fromClassStudents.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">No active students in this class.</p>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {fromClassStudents.map((s: any) => {
                    const checked = selectedStudentIds.has(s.id);
                    return (
                      <li key={s.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleStudent(s.id)}>
                        <input type="checkbox" checked={checked} onChange={() => toggleStudent(s.id)} className="w-4 h-4" />
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">
                          {((s.first_name?.[0] ?? '') + (s.last_name?.[0] ?? '')).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {s.first_name} {s.last_name}
                          </p>
                          <p className="text-xs text-slate-500">{s.admission_no}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                <p className="text-xs text-slate-600">
                  {selectedStudentIds.size === 0
                    ? <><Bell className="w-3 h-3 inline mr-1" /> Moves <b>all {fromClassStudents.length}</b> students. Parents will be notified.</>
                    : <><Bell className="w-3 h-3 inline mr-1" /> Moves <b>{selectedStudentIds.size}</b> selected. Parents will be notified.</>}
                </p>
                <button
                  disabled={!manualFromClassId || (manualDecision !== 'graduate' && !manualToClassId) || manualMut.isPending || fromClassStudents.length === 0}
                  onClick={async () => {
                    const count = selectedStudentIds.size || fromClassStudents.length;
                    if (!(await askConfirm(`Move ${count} student(s) — ${manualDecision}? Parents will receive a notification.`))) return;
                    manualMut.mutate();
                  }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {manualMut.isPending ? 'Moving…' : `Apply ${manualDecision}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          AUTO MODE (existing annual results flow)
          ═══════════════════════════════════════════════════════ */}
      {mode === 'auto' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Auto promotion from annual results</h2>
            <p className="text-sm text-slate-500">
              Requires annual results to be computed first. Based on each student's average and the pass mark, they are promoted or repeat.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
                <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Select class…</option>
                  {classes.map((c: any) => <option key={c.id} value={c.id}>{fmtClass(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
                <select value={sessionId} onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Select session…</option>
                  {sessions.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">To class</label>
                <select value={targetClassId} onChange={(e) => setTargetClassId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Auto</option>
                  {classes.map((c: any) => <option key={c.id} value={c.id}>{fmtClass(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Pass mark</label>
                <input type="number" value={passMark} onChange={(e) => setPassMark(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isGraduation} onChange={(e) => setIsGraduation(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm text-slate-700">Graduation run (final-year class)</span>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              {!isGraduation ? (
                <button disabled={!classId || !sessionId || previewM.isPending} onClick={() => previewM.mutate()}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
                  <Play className="w-4 h-4" /> {previewM.isPending ? 'Previewing…' : 'Preview'}
                </button>
              ) : (
                <button disabled={!classId || !sessionId || gradM.isPending}
                  onClick={async () => { if (await askConfirm('Graduate all final-year students in this class?')) gradM.mutate(); }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" /> {gradM.isPending ? 'Running…' : 'Run graduation'}
                </button>
              )}
            </div>
          </div>

          {preview && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">Preview</h2>
                <button disabled={applyM.isPending}
                  onClick={async () => { if (await askConfirm('Apply these promotions?')) applyM.mutate(); }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {applyM.isPending ? 'Applying…' : 'Apply'}
                </button>
              </div>
              {preview.counts && (
                <div className="grid grid-cols-3 gap-3">
                  <StatChip label="Promoted" value={preview.counts.promoted ?? 0} tone="green" />
                  <StatChip label="Repeat" value={preview.counts.repeat ?? 0} tone="amber" />
                  <StatChip label="Graduate" value={preview.counts.graduate ?? 0} tone="blue" />
                </div>
              )}
              {arr<any>(preview.students).length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No students to preview.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Student</th>
                      <th className="px-4 py-3 text-left">Average</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {arr<any>(preview.students).map((p: any) => (
                      <tr key={p.student_id}>
                        <td className="px-4 py-3 font-medium">{p.name}</td>
                        <td className="px-4 py-3">{p.average}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                            p.promotion_status === 'promoted' ? 'bg-green-50 text-green-700' :
                            p.promotion_status === 'repeat' ? 'bg-amber-50 text-amber-700' :
                            p.promotion_status === 'graduate' ? 'bg-blue-50 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>{p.promotion_status ?? '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Promotion history</h2>
            {history.length === 0 ? (
              <p className="text-sm text-slate-500">No promotions recorded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {history.map((h: any) => (
                  <li key={h.id} className="py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {h.from_class_name ?? h.from_class ?? '—'}
                        <ArrowRight className="w-3 h-3 inline mx-1 text-slate-400" />
                        {h.to_class_name ?? h.to_class ?? '—'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {h.session_name ?? ''} · {h.count ?? h.students ?? 0} students · {h.created_at ? new Date(h.created_at).toLocaleDateString('en-GB') : '—'}
                      </p>
                    </div>
                    <button disabled={revertM.isPending}
                      onClick={async () => { if (await askConfirm('Revert this promotion run?')) revertM.mutate(h.id); }}
                      className="text-xs font-semibold text-red-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                      <RotateCcw className="w-3 h-3" /> Revert
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, tone }: { label: string; value: number; tone: 'green' | 'amber' | 'blue' }) {
  const tones = {
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <p className="text-xs font-semibold">{label}</p>
      <p className="text-2xl font-extrabold">{value}</p>
    </div>
  );
}

function BarChartIcon() {
  return <svg className="w-4 h-4 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="8" /><rect x="12" y="6" width="3" height="12" /><rect x="17" y="13" width="3" height="5" /></svg>;
}
